import React, { useState } from "react";

import { Button, Divider } from "@mui/material";
import { Stack } from "@mui/system";
import { useTranslation } from "react-i18next";

import { verifyDoctorByID } from "../../../Api/Doctor/Doctor.Api";
import { ReactComponent as Calendar } from "../../../Assets/Images/Calendar.svg";
import { ReactComponent as DoctorTwo } from "../../../Assets/Images/Doctor2.svg";
import { ReactComponent as Email } from "../../../Assets/Images/Email.svg";
import { ReactComponent as GraduationCap } from "../../../Assets/Images/GraduationCap.svg";
import { ReactComponent as CorrectIcon } from "../../../Assets/Images/Icon/CorrectICONTwo.svg";
import { ReactComponent as ID } from "../../../Assets/Images/Id.svg";
import { ReactComponent as LocationTwo } from "../../../Assets/Images/Location2.svg";
import { ReactComponent as Phone } from "../../../Assets/Images/Phone.svg";
import { ReactComponent as Stethoscope } from "../../../Assets/Images/Stethoscope.svg";
import { AddDoctor } from "../../../Components/organisms/AddDoctor/AddDoctor";
import { NewUser } from "../../../Components/organisms/User/NewUser";
import useOverlayState from "../../../Utils/Hooks/useOverlayState";
import { formatPhoneNumber } from "../../../Utils/PhoneNumber/PhoneNumber";
import { SideDrawer } from "../../atoms/Drawer/SideDrawer";
import { LoadingButtonType } from "../../atoms/LoadingButton/LoadingButton";
import { Heading5 } from "../../atoms/Typography/Heading/Heading";
import {
  SubHeader,
  SubHeader2,
} from "../../atoms/Typography/SubHeader/SubHeader";
import { DoctorProfile } from "../../templates/DoctorProfile/DoctorProfile";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";

export const ViewDoctorDetails = ({ close, data, OpenDrawer, loadDoctors }) => {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { OverlayState, openOverlay, closeOverlay } = useOverlayState();
  const [displayDocProfile, setDisplayDocProfile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [verifiedTempStatus, setVerifiedTempStatus] = useState(false);
  const rawUserName = [data?.UserName, data?.userName].find((value) =>
    typeof value === "string" ? value.trim() !== "" : value,
  );
  const userName =
    typeof rawUserName === "string" ? rawUserName.trim() : rawUserName || "-";

  // Verify doctor function
  const VerifyDoctor = async () => {
    setLoading(true);
    try {
      let response = await verifyDoctorByID(data.id);
      if (response.type === 0) {
        setLoading(false);
        setAlertMessage(t("validation:doctor-verify-success"));
        openOverlay({ OverlayName: "verify-doctor" });
        setVerifiedTempStatus(true);
        loadDoctors();
      } else {
        setLoading(false);
        setAlertMessage(t("validation:doctor-verify-error"));
        openOverlay({ OverlayName: "verify-doctor", mode: "fail" });
      }
    } catch (exception) { }
  };


  return (
    <>
      <SideDrawer
        headingText={t("glossary:doctor-details.doctor-details")}
        width={"648px"}
        openDrawer={OpenDrawer}
        close={close}
        anchorDrawer={"right"}
      >
        <div>
          <Stack marginLeft={"24px"} rowGap={"20px"}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2,1fr)",
                gap: "16px",
              }}
            >
              <Stack>
                <SubHeader
                  icon={<Calendar />}
                  text={t("common:table-field.created-date")}
                />
                <SubHeader2 text={data.createDate || "-"} />
              </Stack>

              <Stack>
                <SubHeader
                  icon={<ID />}
                  text={t("glossary:doctor-details.user-id")}
                />
                <SubHeader2 text={data.docId || "-"} />
              </Stack>

              <Stack>
                <SubHeader
                  icon={<ID />}
                  text={t("glossary:doctor-details.prof-license-no")}
                />
                <SubHeader2 text={data.professionalLiceneseNumber || "-"} />
              </Stack>

              <Stack>
                <SubHeader
                  icon={<ID />}
                  text={t("glossary:doctor-details.registration-no")}
                />
                <SubHeader2 text={data.registrationNo || "-"} />
              </Stack>

              <Stack>
                <SubHeader
                  icon={<DoctorTwo />}
                  text={t("common:table-field.name")}
                />
                <SubHeader2 text={data.fullName || "-"} />
              </Stack>

              <Stack>
                <SubHeader
                  icon={<ID />}
                  text={t("glossary:doctor-details.user-name")}
                />
                <SubHeader2 text={userName} />
              </Stack>

              <Stack>
                <SubHeader
                  icon={<Phone />}
                  text={t("glossary:doctor-details.mobile-no")}
                />
                <SubHeader2
                  text={
                    data.phoneNumber
                      ? data?.phoneNumberDialCode +
                      " " +
                      formatPhoneNumber(data.phoneNumber)
                      : "-"
                  }
                />
              </Stack>

              <Stack>
                <SubHeader icon={<Email />} text={t("common:form.email")} />
                <SubHeader2 text={data.email || "-"} />
              </Stack>

              <Stack>
                <SubHeader
                  icon={<GraduationCap />}
                  text={t("glossary:doctor-details.university")}
                />
                <SubHeader2
                  text={Array.isArray(data.university) && data.university ?
                    data.university.map((i) => i.university)
                      .join(", ")
                    : "-"

                  }
                />
              </Stack>

              <Stack>
                <SubHeader
                  icon={<GraduationCap />}
                  text={t("glossary:doctor-details.uni-specialty")}
                />
                <SubHeader2 text={Array.isArray(data.universitySpeciality) && data.universitySpeciality ?
                  data.universitySpeciality.map((i) => i.qualification)
                    .join(", ")
                  : "-"} />
              </Stack>

              <Stack>
                <SubHeader
                  icon={<Stethoscope />}
                  text={t("glossary:doctor-details.specialty")}
                />
                <SubHeader2
                  text={
                    data.speciality && data.speciality.length > 0
                      ? data.speciality
                        .map((specialty) => specialty.description || specialty.Description)
                        .join(", ")
                      : "-"
                  }
                />
              </Stack>

              <Stack>
                <SubHeader
                  icon={<Stethoscope />}
                  text={t("glossary:doctor-details.specialty-no")}
                />
                <SubHeader2
                  text={
                    Array.isArray(data.specialityNumber) && data.specialityNumber ?
                      data.specialityNumber.map((i) => i.specialityNumber)
                        .join(", ")
                      : "-"
                  }
                />
              </Stack>

              <Stack>
                <SubHeader
                  icon={<LocationTwo />}
                  text={t("glossary:doctor-details.address")}
                />

                <SubHeader2
                  text={data.address.trim() === "" ? "-" : data.address.trim()}
                />
              </Stack>
            </div>

            <Divider style={{ width: "100%", marginBottom: "20px" }} />

            <Stack
              direction={"row"}
              spacing={2}
              width={"100%"}
              justifyContent={"flex-end"}
              marginTop={"19px"}
            >
              <LoadingButtonType
                style={{
                  color: "#FFFFFF",
                  maxWidth: "150px",
                  paddingTop: "12px",
                  paddingBottom: "12px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                }}
                loading={false}
                variant="lightBlue"
                type="submit"
                onClick={() => {
                  setDisplayDocProfile(true);
                }}
              >
                {t("glossary:doctor-details.preview-profile")}
              </LoadingButtonType>

              <LoadingButtonType
                style={{
                  color: "#FFFFFF",
                  maxWidth: "150px",
                  paddingTop: "12px",
                  paddingBottom: "12px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                }}
                loading={false}
                variant="lightBlue"
                type="submit"
                onClick={() => {
                  openOverlay({ OverlayName: "update-doc", mode: "update" });
                }}
              >
                {t("glossary:doctor-details.update-profile")}
              </LoadingButtonType>

              {!data.verifyDoctor && !verifiedTempStatus ? (
                <LoadingButtonType
                  style={{
                    maxWidth: "150px",
                    paddingTop: "12px",
                    paddingBottom: "12px",
                    paddingLeft: "20px",
                    paddingRight: "20px",
                    fontSize: "13px",
                  }}
                  loading={loading}
                  variant="primaryBlue"
                  type="submit"
                  onClick={() => VerifyDoctor()}
                >
                  {t("glossary:doctor-details.verify-doctor-caps")}
                </LoadingButtonType>
              ) : (
                <Button
                  variant="lightBlue"
                  sx={{ background: "#2A9F47" }}
                  onClick={() => { }}
                >
                  <CorrectIcon style={{ marginRight: "10px" }} />
                  <Heading5
                    text={t("glossary:doctor-details.verified-doctor")}
                    color={"#FFFFFF"}
                  />
                </Button>
              )}
            </Stack>
          </Stack>
        </div>
        {drawerOpen && (
          <SideDrawer
            openDrawer={drawerOpen}
            setDrawerOpen={setDrawerOpen}
            close={() => {
              setDrawerOpen(!drawerOpen);
            }}
            anchorDrawer="right"
            width={"648px"}
            headingText={"Update Doctor"}
          >
            <NewUser control={data} />
          </SideDrawer>
        )}
        {OverlayState.visible && OverlayState.OverlayName === "update-doc" && (
          <AddDoctor
            closeMain={close}
            close={closeOverlay}
            OpenDrawer={OverlayState.visible}
            mode={OverlayState.mode}
            data={data}
            loadDoctors={loadDoctors}
          />
        )}
      </SideDrawer>

      {/* Preview detailed doctor profile */}
      <DoctorProfile
        open={displayDocProfile}
        setOpen={setDisplayDocProfile}
        docId={data.id}
        verifiedDocStatus={verifiedTempStatus}
      />

      {/* Alert/confirm box */}
      {OverlayState.visible && OverlayState.OverlayName === "verify-doctor" && (
        <ConfirmDialog
          isError={OverlayState.mode === "fail"}
          title={t("glossary:doctor-details.verify-doctor")}
          description={alertMessage}
          open={OverlayState?.visible}
          close={() => {
            closeOverlay();
          }}
          onOkClick={() => {
            closeOverlay();
          }}
        />
      )}
    </>
  );
};
